"use client";

import { useCallback, useContext, useMemo } from "react";

import log from "logbeacon";
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { useParams } from "next/navigation";
import { rootStore } from "../store";
import { QUERY_KEYS } from "../config";
import {
  AgentReport,
  AgentReportCategoryEnum,
  AgentReportLevelEnum,
  AgentReportPagination,
  ListAgentReportRequest,
} from "../features/api/memberApi";
import { I18nUtil } from "../utils";
import { OspApiClientContext } from "@features";

const DEFAULT_PAGE_SIZE = 20;

type IUseAgentReportListProps = {
  /** 查询参数 */
  params?: {
    /** agent report category */
    category?: AgentReportCategoryEnum;
    /** agent id list */
    agent_ids?: string[];
    /** include id list */
    ids?: string[];
    /** levels */
    levels?: AgentReportLevelEnum[];
    /** market */
    market?: string;
    /** @default false */
    count_today?: boolean;
    /**
     * If true, only return reports from the last 24 hours
     * @default false
     */
    last_24h?: boolean;
    /**
     * If true, only return asset-related reports
     * @default false
     */
    asset_only?: boolean;
    "enrich.enable"?: boolean;
    "enrich.with_asset.enable"?: boolean;
    "enrich.with_asset.with_price"?: boolean;
  };
  /** 每页数量 */
  pageSize?: number;
  /** 是否启用查询 */
  enabled?: boolean;
  /** 为 true 时未登录也可请求（仅用于 All 等公开数据）；Watchlist 仍需登录 */
  allowUnauthenticated?: boolean;
  staleTime?: number;
  initialData?: AgentReportPagination;
};

export const useAgentReportList = (props?: IUseAgentReportListProps) => {
  const { memberApi } = useContext(OspApiClientContext);
  const { locale } = useParams<{ locale: string }>();
  const {
    params = {},
    pageSize = DEFAULT_PAGE_SIZE,
    enabled = true,
    allowUnauthenticated = false,
    staleTime = 0,
    initialData,
  } = props || {};
  const {
    isLoggedIn,
    isLoading: isLoginLoading,
    isFetching: isLoginFetching,
  } = useSnapshot(rootStore.login);

  const requestParams = useMemo(
    () =>
      ({
        language: I18nUtil.getBackgroundLanguageKey(locale),
        ...params,
      }) as ListAgentReportRequest,
    [params, locale],
  );

  // 优化queryKey构建，只使用稳定的标识符，避免对象引用变化
  const queryKey = useMemo(() => {
    // 构建稳定的queryKey，只使用必要的标识信息
    const paramsKey = JSON.stringify(requestParams); // 序列化params确保一致性

    return [
      QUERY_KEYS.AGENT_REPORT_LIST,
      isLoggedIn,
      allowUnauthenticated,
      paramsKey,
      pageSize,
      enabled,
    ];
  }, [isLoggedIn, allowUnauthenticated, requestParams, pageSize, enabled]);

  // 缓存initialPageParam，避免每次都创建新对象
  const memoizedInitialPageParam = useMemo(
    () => ({
      limit: pageSize,
      next_token: undefined as string | undefined,
    }),
    [pageSize],
  );

  // 使用 useCallback 缓存 select 函数，避免每次组件重新渲染时都创建新的函数引用
  // 这样可以防止 React Query 频繁重新执行 select 函数
  const selectFn = useCallback(
    (
      data: InfiniteData<
        AgentReportPagination | undefined,
        {
          limit: number;
          next_token: string | undefined;
        }
      >,
    ) => {
      log.debug("[useAgentReportList] select data:", data?.pages);
      const reports = data?.pages
        ?.map((page: AgentReportPagination | undefined) => page?.rows)
        .flat()
        ?.filter((item: AgentReport | undefined) => !!item) as
        AgentReport[] | undefined;

      return reports;
    },
    [],
  ); // 将 count_today 作为依赖项，当它变化时重新创建 select 函数

  const {
    data,
    isLoading,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    enabled:
      !!memberApi &&
      enabled &&
      (isLoggedIn || allowUnauthenticated) &&
      !isLoginLoading &&
      !isLoginFetching,
    staleTime,
    initialPageParam: memoizedInitialPageParam,
    initialData: initialData
      ? {
          pages: [initialData],
          pageParams: [memoizedInitialPageParam],
        }
      : undefined,
    queryFn: async ({ pageParam }) => {
      log.debug(
        "[useAgentReportList] start with requestParams:",
        requestParams,
      );
      const res = await memberApi?.agents
        .listAgentReport({
          ...requestParams,
          limit: pageParam.limit,
          next_token: pageParam.next_token,
        } as any)
        .catch((error) => {
          log.error("[useAgentReportList] API error:", error);
          return Promise.reject(error);
        });

      if (res?.error) {
        log.error("[useAgentReportList] response error:", res.error);
        return Promise.reject(res.error);
      }
      log.debug("[useAgentReportList] response data:", res?.data?.data);
      return res?.data?.data as AgentReportPagination | undefined;
    },
    getNextPageParam: (lastPage, _, lastPageParam) => {
      // 检查是否有数据
      if (!lastPage?.rows?.length) {
        return undefined;
      }

      // 如果存在 next_token，说明还有更多数据
      if (lastPage.next_token) {
        return {
          ...lastPageParam,
          next_token: lastPage.next_token,
        };
      }

      // 如果当前页数据少于页面大小，说明没有更多数据了
      const currentPageData = lastPage.rows;
      if (currentPageData.length < pageSize) {
        return undefined;
      }

      // 默认返回 undefined，表示没有更多数据
      return undefined;
    },
    select: selectFn,
  });

  return {
    data: data || [],
    isLoading,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    refetch,
  };
};
